import { lazy, Suspense, useEffect } from 'react'
import { PageLoader } from '@cinqo/ui'
import { useAuth } from '../lib/auth'
import { bootIntercom, shutdownIntercom } from '../lib/intercom'

/* Lazy imports : evite de casser le code splitting de App.tsx.
   Sans ca, ClientPortal et PartnerPortal seraient bundles avec
   ImpersonationPanel (Shell regulateur) et voleraient tout le code
   des autres roles dans le chunk regulateur. */
const ClientPortal = lazy(() => import('../pages/ClientPortal'))
const PartnerPortal = lazy(() => import('../pages/PartnerPortal'))

/* ------------------------------------------------------------------ */
/*  ImpersonationPanel                                                  */
/*  Renders the actual Client/Partner Portal in impersonation mode      */
/* ------------------------------------------------------------------ */

function ImpersonationLoading() {
  return <PageLoader height="full" />
}

interface ImpersonationPanelProps {
  type: 'client' | 'partner'
  id: string
  name: string
  onClose: () => void
}

export default function ImpersonationPanel({
  type,
  id,
  name,
  onClose,
}: ImpersonationPanelProps) {
  const { user } = useAuth()

  // La bulle Intercom est retiree de l'admin : on la remet le temps de
  // l'impersonation pour voir l'ecran tel que le client le voit. Elle reste
  // a l'identite du membre de l'equipe, pas a celle du client.
  useEffect(() => {
    if (!user) return
    bootIntercom(user)
    return () => shutdownIntercom()
  }, [user])

  if (type === 'client') {
    return (
      <Suspense fallback={<ImpersonationLoading />}>
        <ClientPortal
          impersonateAs={{ client_id: id, client_name: name }}
          onExitImpersonation={onClose}
        />
      </Suspense>
    )
  }

  // Partner impersonation: show partner portal with impersonation banner
  return (
    <div className="min-h-screen bg-surface-card">
      {/* Impersonation banner — décalé de la largeur du rail (md:ml-60) pour ne
          pas recouvrir le logo Cinqo de la sidebar (fixed top-0). Plein écran en mobile. */}
      <div className="sticky top-0 z-[60] md:ml-60 bg-mint-700 text-white px-4 sm:px-6 py-2.5 flex items-center gap-3">
        <button
          onClick={onClose}
          className="flex items-center gap-1.5 text-white/90 hover:text-white cursor-pointer transition-colors"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          <span className="text-caption font-medium">Ops</span>
        </button>
        <div className="w-px h-4 bg-surface-card/30" />
        <svg className="w-4 h-4 flex-shrink-0 text-mint-200" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
        <span className="text-data font-semibold">
          Simulation partenaire - {name}
        </span>
      </div>
      <Suspense fallback={<ImpersonationLoading />}>
        <PartnerPortal impersonatePartnerId={id} />
      </Suspense>
    </div>
  )
}
